import classNames from "classnames";
import { PureComponent, ReactNode } from "react";
import { REDUCED_MOTION_QUERY } from "../../../shared/hooks/usePrefersReducedMotion";
import ChevronDownSvg from "../../../svgs/small-icons/chevron-down.svg";
import KuikaBadge from "../kuika-badge/kuika-badge";
import KuikaNumber from "../kuika-number/kuika-number";
import "./kuika-panel-accordion.scss";

interface Props {
  label: string;
  children?: ReactNode;
  isOpen?: boolean;
  onChange?: (value: boolean) => void;
  /**
   * How many items the section holds, shown as a neutral chip beside the label. It is a
   * separate prop rather than part of `label` because the test id and the onboarding id are
   * both derived from `label` — folding a count into it would change them on every add.
   */
  count?: number;
  [key: string]: any;
}

/** `opening` / `closing` while the content's grid row animates; `idle` at rest. */
type AccordionMotion = "idle" | "opening" | "closing";

interface State {
  isOpen: boolean;
  motion: AccordionMotion;
}

const prefersReducedMotion = (): boolean =>
  typeof window.matchMedia === "function" && window.matchMedia(REDUCED_MOTION_QUERY).matches;

/**
 * A panel section with an uppercase heading that opens and closes its content. The content's grid
 * row animates 0fr ↔ 1fr on --dur-base; while it closes the content stays mounted, and it is
 * unmounted once the motion ends (closed content is not in the DOM, as before). A section open on
 * first render appears without the motion, and under reduced motion it opens and closes at once.
 */
class KuikaPanelAccordion extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      // Taken from the prop at once, so a section that starts open does not play an opening.
      isOpen: props.isOpen === true,
      motion: "idle"
    };
  }

  private setOpen = (isOpen: boolean): void => {
    if (isOpen === this.state.isOpen) return;

    let motion: AccordionMotion = "idle";
    if (!prefersReducedMotion()) {
      motion = isOpen ? "opening" : "closing";
    }

    this.setState({ isOpen, motion });
  };

  private syncIsOpen = (): void => {
    if (this.props.isOpen === undefined) return;

    this.setOpen(this.props.isOpen);
  };

  private onClickAccordionButton = (): void => {
    const isOpen: boolean = !this.state.isOpen;
    this.setOpen(isOpen);
    this.props.onChange?.(isOpen);
  };

  private onContentMotionEnd = (event: React.AnimationEvent<HTMLDivElement>): void => {
    // Animations inside the content bubble here too; only the frame's own motion ends it.
    if (event.target !== event.currentTarget) return;

    this.setState({ motion: "idle" });
  };

  componentDidMount(): void {
    this.syncIsOpen();
  }

  componentDidUpdate(): void {
    this.syncIsOpen();
  }

  render() {
    const { isOpen, motion } = this.state;
    const isContentMounted: boolean = isOpen || motion === "closing";
    const chevronClassNames: string = classNames("kuika-panel-accordion--chevron", {
      "kuika-panel-accordion--chevron-open": isOpen
    });

    return (
      <div className="kuika-panel-accordion">
        <button
          type="button"
          data-testid={`${this.props.label}DropdownButton`}
          data-onboarding-id={`ui-kit_panel-accordion_${this.props.label}`}
          className="kuika-panel-accordion--button"
          aria-expanded={isOpen}
          onClick={this.onClickAccordionButton}
        >
          <span className="kuika-panel-accordion--label">
            {this.props.label}
            {this.props.count !== undefined && (
              <KuikaBadge content={<KuikaNumber value={this.props.count} />} color="grey" size="small" />
            )}
          </span>
          <div className={chevronClassNames}>
            <ChevronDownSvg />
          </div>
        </button>
        {isContentMounted && (
          <div
            className={classNames("kuika-panel-accordion-frame", {
              "kuika-panel-accordion-frame--opening": motion === "opening",
              "kuika-panel-accordion-frame--closing": motion === "closing"
            })}
            onAnimationEnd={this.onContentMotionEnd}
          >
            <div className="kuika-panel-accordion-content">{this.props.children}</div>
          </div>
        )}
      </div>
    );
  }
}

export default KuikaPanelAccordion;
